import { AccessControlLockIcon } from "@gadgetinc/widgets/src/icons/AccessControlLockIcon";
import { AutocomponentIcon } from "@gadgetinc/widgets/src/icons/AutocomponentIcon";
import { GellyIcon } from "@gadgetinc/widgets/src/icons/GellyIcon";
import { NodeJSIcon } from "@gadgetinc/widgets/src/icons/NodeJSIcon";
import { ReactIcon } from "@gadgetinc/widgets/src/icons/ReactIcon";
import { ShopifyWebhookIcon } from "@gadgetinc/widgets/src/icons/ShopifyWebhookIcon";
import { TerminalIcon } from "@gadgetinc/widgets/src/icons/TerminalIcon";
import { ThirdPartyIcon } from "@gadgetinc/widgets/src/icons/ThirdPartyIcon";
import { useStyletron } from "baseui";
import { HeadingXLarge, ParagraphLarge } from "baseui/typography";
import React from "react";
import type { DocsInterfaceProps } from "../../src/chrome/DocsInterface";
import { DocsInterface } from "../../src/chrome/DocsInterface";
import { APIReferenceCalloutLink } from "../../src/components/APIReferenceCalloutLink";
import { SplashCard, SplashCardGrid, SplashContentContainer, colors } from "../../src/components/SplashPage";

const ReferenceLandingPage = (props: DocsInterfaceProps) => {
  const [css, $theme] = useStyletron();

  return (
    <DocsInterface {...props} showLeftSideNav={true}>
      <SplashContentContainer $style={{ maxWidth: "980px" }}>
        <HeadingXLarge>Reference</HeadingXLarge>
        <ParagraphLarge
          $style={{
            paddingTop: $theme.sizing.scale800,
            paddingBottom: $theme.sizing.scale800,
          }}
        >
          API references and information for the packages used to build Gadget apps.
        </ParagraphLarge>
        <APIReferenceCalloutLink />
        <SplashCardGrid $maxColumns={2} className={css({ marginTop: $theme.sizing.scale1200 })}>
          <SplashCard
            icon={<ReactIcon $size={32} />}
            title="@gadget-inc/react"
            color={colors.white}
            body="A package of React hooks & components to seamlessly link a React app with a Gadget backend application."
            href="/reference/react"
            copy="Learn more →"
          />
          <SplashCard
            icon={<AutocomponentIcon $size={32} />}
            title="@gadget-inc/react/auto"
            color={colors.white}
            body="A React library used to power Gadget autocomponents: auto-generated forms and tables pre-configured to read and write data using your app’s API."
            href="/reference/react/auto"
            copy="Learn more →"
          />
          <SplashCard
            title="@gadget-inc/auth"
            icon={<AccessControlLockIcon $size={32} />}
            color={colors.white}
            body="A package using Fastify plugins to implement and manage user authentication for your Gadget application."
            href="/reference/auth"
            copy="Learn more →"
          />
          <SplashCard
            title="gadget-server"
            icon={<NodeJSIcon $size={32} />}
            color={colors.white}
            body="An auto-generated package of utilities to facilitate the creation of the backend for your Gadget application."
            href="/reference/gadget-server"
            copy="Learn more →"
          />
          <SplashCard
            title="Gelly"
            icon={<GellyIcon $size={32} />}
            color={colors.white}
            body="A reference for Gadget's data access language that streamlines query management and boosts performance."
            href="/reference/gelly"
            copy="Learn more →"
          />
          <SplashCard
            title="ggt"
            icon={<TerminalIcon $size={32} />}
            color={colors.white}
            body="A reference for Gadget's command line interface providing additional functionality for working with your applications."
            href="/reference/ggt"
            copy="Learn more →"
          />
          <SplashCard
            title="@gadgetinc/react-shopify-app-bridge"
            icon={<ShopifyWebhookIcon $size={40} />}
            color={colors.white}
            body="A reference for React hooks and components for use with Shopify embedded app development with Gadget."
            href="/reference/shopify-app-bridge"
            copy="Learn more →"
          />
          <SplashCard
            title="@gadgetinc/shopify-extensions"
            icon={<ShopifyWebhookIcon $size={40} />}
            color={colors.white}
            body="A package that helps register session tokens with building Shopify extensions."
            href="/reference/shopify-extensions"
            copy="Learn more →"
          />
          <SplashCard
            title="@gadgetinc/react-bigcommerce"
            icon={<ThirdPartyIcon type="bigcommerce" $size={40} />}
            color={colors.white}
            body="Handles OAuth and session management for single-click BigCommerce apps."
            href="/reference/react-bigcommerce"
            copy="Learn more →"
          />
        </SplashCardGrid>
      </SplashContentContainer>
    </DocsInterface>
  );
};

export default ReferenceLandingPage;
